import React, { useEffect, useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ROLE_BADGE, ROLE_LABELS } from "@/lib/stages";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ROLES = Object.keys(ROLE_LABELS);

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "broker_csr" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/users"); setUsers(data); }
    catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false);
      setForm({ email: "", password: "", name: "", role: "broker_csr" });
      load();
    } catch (e) { toast.error(formatError(e)); } finally { setSaving(false); }
  };

  const changeRole = async (id, role) => {
    try { await api.put(`/users/${id}`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between md:items-end gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-sky-400 mb-1">Admin</div>
          <h1 className="font-display text-4xl font-bold">User Management</h1>
          <p className="text-slate-400 text-sm mt-1">Add unlimited users and assign roles.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-add-user" className="bg-blue-600 hover:bg-blue-500 text-white">
              <Plus size={16} className="mr-2" /> Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
            <DialogHeader><DialogTitle className="font-display text-2xl">New User</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Name</Label>
                <Input data-testid="input-user-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                       className="bg-slate-950 border-slate-700" />
              </div>
              <div>
                <Label>Email</Label>
                <Input data-testid="input-user-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                       className="bg-slate-950 border-slate-700" />
              </div>
              <div>
                <Label>Password</Label>
                <Input data-testid="input-user-password" type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})}
                       className="bg-slate-950 border-slate-700" />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                  <SelectTrigger data-testid="select-user-role" className="bg-slate-950 border-slate-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                    {ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="button-save-user" onClick={create} disabled={saving} className="bg-blue-600 hover:bg-blue-500">
                {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : null} Save User
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="glass-card rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/80 text-slate-400 uppercase text-[10px] tracking-widest">
            <tr>
              <th className="text-left px-5 py-3">Name</th>
              <th className="text-left px-5 py-3">Email</th>
              <th className="text-left px-5 py-3">Role</th>
              <th className="text-left px-5 py-3">Change role</th>
              <th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-slate-800 hover:bg-slate-900/40">
                <td className="px-5 py-3">{u.name}</td>
                <td className="px-5 py-3 text-slate-300 font-mono-ops text-xs">{u.email}</td>
                <td className="px-5 py-3">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${ROLE_BADGE[u.role] || ""}`}>{ROLE_LABELS[u.role] || u.role}</span>
                </td>
                <td className="px-5 py-3">
                  <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)} disabled={u.id === me?.id}>
                    <SelectTrigger data-testid={`select-change-role-${u.id}`} className="w-48 bg-slate-950 border-slate-700 h-8"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                      {ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-5 py-3 text-right">
                  {u.id !== me?.id && (
                    <Button data-testid={`button-delete-user-${u.id}`} size="sm" variant="ghost" onClick={() => del(u.id)}
                            className="text-rose-300 hover:text-rose-200 hover:bg-rose-500/10">
                      <Trash2 size={14} />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
